import { useEffect, useRef, useState } from 'react';

import {
  TenantResolveErrorCode,
  TenantResolveResponse,
  resolveTenant,
  tenantResolveErrorCode,
} from '../api/tenants';

// Matches a canonical GUID (the tenant-ID shape). When the admin pastes a raw
// tenant GUID we skip resolution entirely (backward-compat with the pre-FD-110
// GUID-only flow) — new-migration validates the GUID itself.
const GUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// A permissive hostname heuristic: at least one dot, valid label characters, a
// 2+ char alphabetic TLD. This only gates whether we FIRE a resolve — the server
// is the authority on domain validity (returns 400 INVALID_DOMAIN otherwise), so
// we stay loose and let a plausible-looking domain through while not firing on a
// half-typed fragment.
const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

const DEBOUNCE_MS = 400;

export function isTenantGuid(value: string): boolean {
  return GUID_RE.test(value.trim());
}

export type TenantResolveState =
  // Nothing to resolve yet (empty, or a partial fragment that isn't a GUID or a
  // plausible domain). The submit gate treats this as not-ready for T2T.
  | { kind: 'idle' }
  // The value is a raw tenant GUID — used directly, no network call.
  | { kind: 'guid'; tenantId: string }
  // A resolve request is in flight for a domain.
  | { kind: 'resolving'; domain: string }
  // Domain resolved to a tenant GUID.
  | { kind: 'resolved'; data: TenantResolveResponse }
  // Resolve failed; `code` is the FD-110 machine code (or UNKNOWN).
  | { kind: 'error'; code: TenantResolveErrorCode | 'UNKNOWN'; message: string };

// Debounced domain→tenant resolution for a single tenant input.
//
// - Empty / fragment                → idle
// - Raw GUID                        → guid (no network)
// - Plausible domain                → debounced resolve → resolving → resolved | error
//
// A monotonically increasing request id guards against out-of-order responses:
// only the latest in-flight request may commit state. `enabled` lets the caller
// short-circuit the whole hook (e.g. D2C, where there are no tenants) so no
// resolve ever fires.
export function useTenantResolve(value: string, enabled = true): TenantResolveState {
  const [state, setState] = useState<TenantResolveState>({ kind: 'idle' });
  const requestSeq = useRef(0);

  useEffect(() => {
    const trimmed = value.trim();

    if (!enabled || trimmed.length === 0) {
      requestSeq.current += 1; // cancel any in-flight resolve
      setState({ kind: 'idle' });
      return;
    }

    if (isTenantGuid(trimmed)) {
      requestSeq.current += 1;
      setState({ kind: 'guid', tenantId: trimmed });
      return;
    }

    if (!DOMAIN_RE.test(trimmed)) {
      // Not a GUID and not (yet) a plausible domain — wait for more input.
      requestSeq.current += 1;
      setState({ kind: 'idle' });
      return;
    }

    const domain = trimmed.toLowerCase();
    const seq = ++requestSeq.current;
    const handle = window.setTimeout(() => {
      setState({ kind: 'resolving', domain });
      resolveTenant(domain)
        .then((data) => {
          if (seq === requestSeq.current) setState({ kind: 'resolved', data });
        })
        .catch((err) => {
          if (seq !== requestSeq.current) return;
          setState({
            kind: 'error',
            code: tenantResolveErrorCode(err),
            message: err instanceof Error ? err.message : 'Resolution failed',
          });
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [value, enabled]);

  return state;
}
